/**
 * Turn Illustration Module
 *
 * Handles Turn Art (crayon/pencil sketch) illustration generation for scanned book pages.
 * Includes:
 * - Single page illustration generation (generateTurnIllustration)
 * - Persistent background queue (localStorage-backed, survives navigation)
 * - Server-side background generation (via Edge Function, survives browser close)
 * - Progress tracking from both localStorage and database
 */

import { supabase } from '@/lib/supabase/client';
import { Sentry } from '@/lib/sentry';
import { updateDigitalPagesTurnArt } from './digital-pages';
import { getBookPages } from '../scannedBookService';

// ============================================================================
// Types
// ============================================================================

interface TurnArtQueueItem {
  bookId: string;
  bookTitle: string;
  pendingPages: number[]; // page numbers still to generate
  completedPages: number[];
  failedPages?: Record<number, number>; // page number -> failure count (for retry limiting)
  startedAt: string;
}

// ============================================================================
// Single Page Illustration Generation
// ============================================================================

/**
 * Generate a Turn-style illustration for a page.
 * Uses the page text and optional description to create a consistent
 * crayon/pencil sketch illustration that matches the Turn aesthetic.
 *
 * The generated illustration is stored in Supabase storage and the URL
 * is saved to the page record.
 *
 * @param bookId - The book ID
 * @param pageNumber - The page number
 * @param bookTitle - Optional book title for context
 * @returns The URL of the generated illustration
 */
export async function generateTurnIllustration(
  bookId: string,
  pageNumber: number,
  bookTitle?: string
): Promise<string | null> {
  try {
    // Dynamically import to avoid circular dependencies
    const {
      generateTurnIllustration: generate,
      getOrCreateStoryBible,
    } = await import('@/lib/turn-illustration');

    // Get the page data including original image URL
    const { data: page, error: fetchError } = await supabase
      .from('scanned_book_pages')
      .select('extracted_text, illustration_description, has_illustration, image_url')
      .eq('book_id', bookId)
      .eq('page_number', pageNumber)
      .maybeSingle();

    if (fetchError || !page) {
      console.error(`[TurnIllustration] Failed to fetch page ${pageNumber}:`, fetchError);
      Sentry.captureException(fetchError instanceof Error ? fetchError : new Error(String(fetchError)), {
        tags: { flow: 'scan_pipeline', step: 'turn_illustration' },
      });
      return null;
    }

    const pageText = page.extracted_text || '';

    // Skip if no text AND no image (wordless page with no reference)
    if (!pageText.trim() && !page.illustration_description && !page.image_url) {
      console.log(`[TurnIllustration] Page ${pageNumber} has no content to analyze, skipping`);
      return null;
    }

    // FETCH ALL PAGES for Story Bible (character consistency)
    const { data: allPages } = await supabase
      .from('scanned_book_pages')
      .select('page_number, extracted_text, illustration_description')
      .eq('book_id', bookId)
      .eq('is_superseded', false)
      .order('page_number');

    // Build Story Bible for character consistency across all pages
    const storyBible = await getOrCreateStoryBible(
      bookId,
      bookTitle || 'Untitled Book',
      (allPages || []).map(p => ({
        pageNumber: p.page_number,
        text: p.extracted_text || '',
        illustrationDescription: p.illustration_description || undefined,
      }))
    );

    console.log(`[TurnIllustration] Using Story Bible with ${storyBible.characters.length} characters`);

    // Fetch original image for GPT-4o Vision analysis (if available)
    let originalImageBase64: string | undefined;

    if (page.image_url) {
      try {
        console.log(`[TurnIllustration] Fetching original image for page ${pageNumber}...`);
        const imageResponse = await fetch(page.image_url);
        if (imageResponse.ok) {
          const imageBlob = await imageResponse.blob();
          const reader = new FileReader();
          originalImageBase64 = await new Promise<string>((resolve, reject) => {
            reader.onloadend = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(imageBlob);
          });
          console.log(`[TurnIllustration] Page ${pageNumber}: Original image loaded for analysis`);
        }
      } catch (imgErr) {
        console.warn(`[TurnIllustration] Could not fetch original image for page ${pageNumber}:`, imgErr);
      }
    }

    // Generate the illustration with Story Bible for consistency
    const result = await generate({
      pageText: pageText || page.illustration_description || 'A gentle scene from a children\'s book',
      pageNumber,
      bookTitle,
      originalImageBase64,
      sceneDescription: page.illustration_description || undefined,
      storyBible, // Character consistency!
    });

    // Upload to Supabase storage
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user?.id) {
      throw new Error('Not authenticated');
    }

    // Convert base64 to blob for upload
    const base64Data = result.imageUrl.split(',')[1];
    const mimeType = result.imageUrl.split(';')[0].split(':')[1] || 'image/png';
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: mimeType });

    // Upload to storage
    const fileName = `turn-illustrations/${session.user.id}/${bookId}/page-${pageNumber}-${Date.now()}.png`;
    const { error: uploadError } = await supabase.storage
      .from('scanned-pages')
      .upload(fileName, blob, {
        contentType: mimeType,
        upsert: true,
      });

    if (uploadError) {
      console.error(`[TurnIllustration] Failed to upload illustration:`, uploadError);
      Sentry.captureException(uploadError instanceof Error ? uploadError : new Error(String(uploadError)), {
        tags: { flow: 'scan_pipeline', step: 'turn_illustration' },
      });
      throw uploadError;
    }

    // Get public URL
    const { data: urlData } = supabase.storage
      .from('scanned-pages')
      .getPublicUrl(fileName);

    const illustrationUrl = urlData.publicUrl;

    // Update page record with the Turn illustration URL
    // Using a new column that we'll add: turn_illustration_url
    // For now, store in enhanced_image_url as a temporary measure
    const { error: updateError } = await supabase
      .from('scanned_book_pages')
      .update({
        enhanced_image_url: illustrationUrl,
        // Mark that this is a Turn-generated illustration (vs original enhanced)
        // We can add a proper column later via migration
      } as any)
      .eq('book_id', bookId)
      .eq('page_number', pageNumber);

    if (updateError) {
      console.warn(`[TurnIllustration] Failed to update page record:`, updateError);
      // Don't throw - we still have the URL
    }

    // Get source page ID for digital pages update
    const { data: sourcePageData } = await supabase
      .from('scanned_book_pages')
      .select('id')
      .eq('book_id', bookId)
      .eq('page_number', pageNumber)
      .maybeSingle();

    // Update digital pages with Turn Art URL and analyze text position
    if (sourcePageData?.id) {
      try {
        const { analyzeImageForTextPlacement } = await import('@/lib/turn-illustration');
        const textPositionResult = await analyzeImageForTextPlacement(result.imageUrl);

        await updateDigitalPagesTurnArt(
          sourcePageData.id,
          illustrationUrl
        );
        console.log(`[TurnIllustration] Updated digital pages with Turn Art, text position: ${textPositionResult.position}`);
      } catch (dpErr) {
        console.warn(`[TurnIllustration] Could not update digital pages:`, dpErr);
        // Non-fatal - digital pages are optional
      }
    }

    console.log(`[TurnIllustration] Generated illustration for page ${pageNumber} in ${result.generationTimeMs}ms`);
    return illustrationUrl;

  } catch (error) {
    console.error(`[TurnIllustration] Failed to generate for page ${pageNumber}:`, error);
    Sentry.captureException(error instanceof Error ? error : new Error(String(error)), {
      tags: { flow: 'scan_pipeline', step: 'turn_illustration' },
    });
    return null;
  }
}

// ============================================================================
// Persistent Background Turn Art Generation
// ============================================================================

const TURN_ART_QUEUE_KEY = 'turn-art-generation-queue';

/**
 * Get the current Turn Art generation queue from localStorage
 */
export function getTurnArtQueue(): TurnArtQueueItem[] {
  try {
    const stored = localStorage.getItem(TURN_ART_QUEUE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

/**
 * Save the Turn Art generation queue to localStorage
 */
function saveTurnArtQueue(queue: TurnArtQueueItem[]) {
  try {
    localStorage.setItem(TURN_ART_QUEUE_KEY, JSON.stringify(queue));
  } catch (e) {
    console.warn('[TurnArtQueue] Failed to save queue:', e);
  }
}

/**
 * Check if a book has pending Turn Art generation
 */
export function hasPendingTurnArt(bookId: string): boolean {
  const queue = getTurnArtQueue();
  return queue.some(item => item.bookId === bookId && item.pendingPages.length > 0);
}

/**
 * Get generation progress for a book
 */
export function getTurnArtProgress(bookId: string): { completed: number; total: number } | null {
  const queue = getTurnArtQueue();
  const item = queue.find(q => q.bookId === bookId);
  if (!item) return null;

  const total = item.pendingPages.length + item.completedPages.length;
  return { completed: item.completedPages.length, total };
}

// Active generation tracker (prevents duplicate processing)
const activeGenerations = new Set<string>();

/**
 * Generate Turn-style illustrations for all pages in a book.
 * PERSISTENT: Stores progress in localStorage, can resume after navigation.
 *
 * @param bookId - The book ID
 * @param onProgress - Optional callback for progress updates
 * @returns Number of successfully generated illustrations
 */
export async function generateAllTurnIllustrations(
  bookId: string,
  onProgress?: (completed: number, total: number) => void
): Promise<number> {
  // Prevent duplicate processing
  if (activeGenerations.has(bookId)) {
    console.log(`[TurnArtQueue] Already processing ${bookId}`);
    const progress = getTurnArtProgress(bookId);
    if (progress) onProgress?.(progress.completed, progress.total);
    return 0;
  }

  activeGenerations.add(bookId);

  try {
    // Get book info
    const { data: book } = await supabase
      .from('scanned_books')
      .select('title')
      .eq('id', bookId)
      .maybeSingle();

    const bookTitle = book?.title || 'Untitled';

    // Check if we have a saved queue for this book
    let queue = getTurnArtQueue();
    let queueItem = queue.find(q => q.bookId === bookId);

    if (!queueItem || queueItem.pendingPages.length === 0) {
      // Get all pages that need Turn Art
      const pages = await getBookPages(bookId);
      const pagesNeedingArt = pages.filter(p =>
        !p.enhanced_image_url && // Not already generated
        ((p.extracted_text && p.extracted_text.trim().length > 0) ||
         (p.illustration_description && p.illustration_description.trim().length > 0))
      );

      if (pagesNeedingArt.length === 0) {
        console.log(`[TurnArtQueue] No pages need Turn Art for ${bookId}`);
        return 0;
      }

      // Create new queue item
      queueItem = {
        bookId,
        bookTitle,
        pendingPages: pagesNeedingArt.map(p => p.page_number),
        completedPages: [],
        startedAt: new Date().toISOString(),
      };

      // Update queue
      queue = queue.filter(q => q.bookId !== bookId);
      queue.push(queueItem);
      saveTurnArtQueue(queue);
    }

    const totalPages = queueItem.pendingPages.length + queueItem.completedPages.length;
    let successCount = queueItem.completedPages.length;

    console.log(`[TurnArtQueue] Processing ${queueItem.pendingPages.length} pages for "${bookTitle}"`);
    onProgress?.(successCount, totalPages);

    // Process remaining pages
    while (queueItem.pendingPages.length > 0) {
      const pageNumber = queueItem.pendingPages[0];

      try {
        const result = await generateTurnIllustration(bookId, pageNumber, bookTitle);

        if (result) {
          successCount++;
        }

        // Update queue - move page from pending to completed
        queueItem.completedPages.push(pageNumber);
        queueItem.pendingPages = queueItem.pendingPages.slice(1);

        // Save progress to localStorage
        queue = queue.filter(q => q.bookId !== bookId);
        if (queueItem.pendingPages.length > 0) {
          queue.push(queueItem);
        }
        saveTurnArtQueue(queue);

        onProgress?.(queueItem.completedPages.length, totalPages);

      } catch (error) {
        console.error(`[TurnArtQueue] Failed page ${pageNumber}:`, error);
        Sentry.captureException(error instanceof Error ? error : new Error(String(error)), {
          tags: { flow: 'scan_pipeline', step: 'turn_art_queue' },
        });

        // Track failures per page to prevent infinite retry loops
        if (!queueItem.failedPages) queueItem.failedPages = {};
        const failCount = (queueItem.failedPages[pageNumber] || 0) + 1;
        queueItem.failedPages[pageNumber] = failCount;

        if (failCount >= 3) {
          // Permanent failure after 3 attempts - move to completed (skipped)
          console.warn(`[TurnArtQueue] Page ${pageNumber} permanently failed after ${failCount} attempts, skipping`);
          queueItem.completedPages.push(pageNumber);
          queueItem.pendingPages = queueItem.pendingPages.slice(1);
        } else {
          // Move to end of queue for retry later
          queueItem.pendingPages = [...queueItem.pendingPages.slice(1), pageNumber];
        }

        // BUG FIX: Persist queue state after failure to prevent infinite retry on app restart
        queue = queue.filter(q => q.bookId !== bookId);
        if (queueItem.pendingPages.length > 0) {
          queue.push(queueItem);
        }
        saveTurnArtQueue(queue);
      }

      // Small delay between pages to avoid rate limits
      if (queueItem.pendingPages.length > 0) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }

    // Remove from queue when complete
    queue = queue.filter(q => q.bookId !== bookId);
    saveTurnArtQueue(queue);

    console.log(`[TurnArtQueue] Complete: ${successCount}/${totalPages} for "${bookTitle}"`);
    return successCount;

  } finally {
    activeGenerations.delete(bookId);
  }
}

/**
 * Resume any pending Turn Art generations.
 * Call this on app startup or when returning to library.
 */
export async function resumePendingTurnArtGenerations(
  onProgress?: (bookId: string, completed: number, total: number) => void
): Promise<void> {
  const queue = getTurnArtQueue();

  for (const item of queue) {
    if (item.pendingPages.length > 0) {
      console.log(`[TurnArtQueue] Resuming generation for "${item.bookTitle}" (${item.pendingPages.length} remaining)`);

      // Don't await - let it run in background
      generateAllTurnIllustrations(item.bookId, (completed, total) => {
        onProgress?.(item.bookId, completed, total);
      }).catch(err => {
        console.error(`[TurnArtQueue] Resume failed for ${item.bookId}:`, err);
        Sentry.captureException(err instanceof Error ? err : new Error(String(err)), {
          tags: { flow: 'scan_pipeline', step: 'turn_art_queue_resume' },
        });
      });
    }
  }
}

// ============================================================================
// Background Turn Art Generation (Server-Side)
// ============================================================================

/**
 * Trigger background Turn Art generation via Edge Function.
 *
 * FIRE-AND-FORGET: This function returns immediately, generation continues
 * on the server. Use Realtime subscriptions to track progress.
 *
 * Benefits:
 * - Survives browser close
 * - Non-blocking UI - user can preview/edit immediately
 * - Server handles retries and rate limiting
 *
 * @param bookId - The book ID to generate Turn Art for
 * @returns Promise that resolves when the trigger is sent (NOT when generation completes)
 */
export async function triggerTurnArtGeneration(bookId: string): Promise<{
  success: boolean;
  error?: string;
}> {
  try {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

    // Get auth token for the Edge Function
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) {
      console.warn('[TurnArt] No auth session, cannot trigger background generation');
      return { success: false, error: 'Not authenticated' };
    }

    console.log(`[TurnArt] Triggering background generation for book ${bookId}`);

    // Fire-and-forget: don't await the full response
    fetch(`${supabaseUrl}/functions/v1/generate-turn-art`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ book_id: bookId }),
    }).then(response => {
      if (!response.ok) {
        console.error(`[TurnArt] Background generation trigger failed: ${response.status}`);
        Sentry.captureException(new Error(`TurnArt background generation trigger failed: ${response.status}`), {
          tags: { flow: 'scan_pipeline', step: 'turn_art_trigger' },
        });
      } else {
        console.log(`[TurnArt] Background generation started for book ${bookId}`);
      }
    }).catch(err => {
      console.error(`[TurnArt] Background generation trigger error:`, err);
      Sentry.captureException(err instanceof Error ? err : new Error(String(err)), {
        tags: { flow: 'scan_pipeline', step: 'turn_art_trigger' },
      });
    });

    return { success: true };

  } catch (error) {
    console.error('[TurnArt] Failed to trigger background generation:', error);
    Sentry.captureException(error instanceof Error ? error : new Error(String(error)), {
      tags: { flow: 'scan_pipeline', step: 'turn_art_trigger' },
    });
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}

/**
 * Get the current Turn Art status for a book from the database.
 *
 * @param bookId - The book ID
 * @returns The turn_art_status value or null if not found
 */
export async function getTurnArtStatus(bookId: string): Promise<'pending' | 'generating' | 'complete' | 'failed' | null> {
  const { data, error } = await supabase
    .from('scanned_books')
    .select('turn_art_status')
    .eq('id', bookId)
    .maybeSingle() as { data: { turn_art_status: string | null } | null; error: any };

  if (error || !data) {
    console.warn('[TurnArt] Failed to get status:', error);
    return null;
  }

  return data.turn_art_status as 'pending' | 'generating' | 'complete' | 'failed' | null;
}

/**
 * Get Turn Art generation progress for a book.
 * Counts pages with and without enhanced_image_url.
 *
 * @param bookId - The book ID
 * @returns Progress object with completed and total counts
 */
export async function getTurnArtProgressFromDb(bookId: string): Promise<{
  completed: number;
  total: number;
  status: 'pending' | 'generating' | 'complete' | 'failed' | null;
}> {
  // Get book status
  const { data: book } = await supabase
    .from('scanned_books')
    .select('turn_art_status')
    .eq('id', bookId)
    .maybeSingle() as { data: { turn_art_status: string | null } | null; error: any };

  // Count pages with content (need Turn Art)
  const { data: pages } = await supabase
    .from('scanned_book_pages')
    .select('page_number, enhanced_image_url, extracted_text, illustration_description')
    .eq('book_id', bookId)
    .eq('is_superseded', false);

  const pagesWithContent = (pages || []).filter(p =>
    (p.extracted_text && p.extracted_text.trim()) ||
    (p.illustration_description && p.illustration_description.trim())
  );

  const completed = pagesWithContent.filter(p => p.enhanced_image_url).length;
  const total = pagesWithContent.length;

  return {
    completed,
    total,
    status: book?.turn_art_status as 'pending' | 'generating' | 'complete' | 'failed' | null,
  };
}
